import { api } from '../../lib/api';

/**
 * Servicio API para recetas de proceso: plantillas e instancias.
 * Base path: /process/recipe/
 */

// TODO: poner a true para usar datos de prueba sin conectar al PLC
const USE_DUMMY_READ_FROM_PLC = false;

/**
 * Aplana el listado agrupado by-line en opciones para selectores.
 * @param {Array} instanceGroups
 * @returns {Array<{ instanceId: number, label: string, productName: string, alternative: string, revision: string }>}
 */
export function flattenInstanceOptions(instanceGroups) {
  if (!Array.isArray(instanceGroups)) return [];
  const options = [];
  for (const g of instanceGroups) {
    const productName = g.product_name ?? g.ProductName ?? '—';
    const alternatives = g.alternatives ?? g.Alternatives ?? [];
    for (const alt of alternatives) {
      const alternative = alt.alternative ?? alt.Alternative ?? '—';
      const revisions = alt.revision ?? alt.Revision ?? alt.revisions ?? [];
      for (const rev of revisions) {
        const instanceId = rev.instance_id ?? rev.InstanceID ?? rev.instanceId;
        const revision = rev.revision ?? rev.Revision ?? '—';
        if (instanceId == null) continue;
        options.push({
          instanceId: Number(instanceId),
          label: `${productName} / ${alternative} / ${revision}`,
          productName,
          alternative,
          revision: String(revision),
        });
      }
    }
  }
  return options;
}

/**
 * Mapea la respuesta de read-from-plc a valores de formulario por parameter_id.
 * @param {object} plcResponse
 * @param {Array<{ id?: number, ID?: number }>} templateParameters
 * @param {'actual'|'recipe'} valueField
 * @returns {Record<number, string>}
 */
export function mapPlcValuesToFormValues(plcResponse, templateParameters, valueField = 'actual') {
  const plcByParamId = new Map();
  for (const row of plcResponse?.values ?? []) {
    const pid = row.parameter_id ?? row.parameterID;
    if (pid == null) continue;
    const raw = valueField === 'recipe' ? row.recipe : row.actual;
    plcByParamId.set(Number(pid), String(raw ?? ''));
  }
  const values = {};
  for (const p of templateParameters ?? []) {
    const id = p.id ?? p.ID;
    if (id == null) continue;
    if (plcByParamId.has(Number(id))) {
      values[id] = plcByParamId.get(Number(id));
    }
  }
  return values;
}

function getDummyReadFromPLCResponse(instanceId) {
  return {
    product_name: 'EX00026143',
    alternative: '1',
    revision: '1',
    description: 'Instancia actualizada - Lote B (datos de prueba)',
    values: [
      { parameter_id: 1, parameter_name: 'Extruder A Z01_01', recipe: '1111.1111', actual: '11.11', unit: 'ºC', data_type: 'float' },
      { parameter_id: 2, parameter_name: 'Extruder A Z01_02', recipe: '0', actual: '0', unit: 'ºC', data_type: 'float' },
      { parameter_id: 3, parameter_name: 'Extruder A Z01_03', recipe: '0', actual: '0', unit: 'ºC', data_type: 'float' },
    ],
  };
}

export const recipeService = {
  // --- Plantillas (templates) ---

  /**
   * Lista plantillas por línea.
   * @param {number} lineTechCode - Identificador de la línea (query: line_tech_code; backend usa como line_asset_id)
   * @returns {Promise<Array>}
   */
  async listTemplatesByLine(lineTechCode) {
    const data = await api.get(
      `/api/process/recipe/templates?line_tech_code=${encodeURIComponent(lineTechCode)}`
    );
    return Array.isArray(data) ? data : [];
  },

  /**
   * Obtiene una plantilla por ID.
   * @param {number} id
   * @returns {Promise<object|null>}
   */
  async getTemplate(id) {
    const data = await api.get(`/api/process/recipe/templates/${id}`);
    return data || null;
  },

  /**
   * Crea una plantilla con parámetros (existentes y/o nuevos).
   * @param {object} body - { name, line_asset_tech_code, description?, version?, parameter_ids?, new_parameters? }
   * @returns {Promise<object>}
   */
  async createTemplateWithParams(body) {
    return await api.post('/api/process/recipe/templates/with-params', body);
  },

  /**
   * Actualiza una plantilla con parámetros.
   * @param {number} id
   * @param {object} body - { name?, description?, version?, parameter_ids?, new_parameters? }
   * @returns {Promise<object>}
   */
  async updateTemplateWithParams(id, body) {
    return await api.put(`/api/process/recipe/templates/${id}/with-params`, body);
  },

  /**
   * Elimina una plantilla (falla si tiene instancias asociadas).
   * @param {number} id
   * @returns {Promise<void>}
   */
  async deleteTemplate(id) {
    return await api.delete(`/api/process/recipe/templates/${id}`);
  },

  // --- Instancias (instances) ---

  /**
   * Lista instancias de una línea agrupadas por producto → alternativa → revisiones (con instance_id).
   * @param {number} lineAssetTechCode - tech_code del activo de línea
   * @returns {Promise<Array<{ product_name, alternatives: Array<{ alternative, revision: Array<{ revision, instance_id }> }> }>>}
   */
  async listInstancesByLine(lineAssetTechCode) {
    const data = await api.get(
      `/api/process/recipe/instances/by-line?line_asset_tech_code=${encodeURIComponent(lineAssetTechCode)}`
    );
    return Array.isArray(data) ? data : [];
  },

  /**
   * Crea una instancia de receta.
   * @param {object} body - { template_id, product_name, alternative, revision, description?, values: [{ parameter_id, value }] }
   * @returns {Promise<object>}
   */
  async createInstance(body) {
    const payload = {
      template_id: body.template_id,
      product_name: body.product_name,
      alternative: body.alternative,
      revision: body.revision,
      description: body.description ?? '',
      values: (body.values ?? []).map((v) => ({ parameter_id: v.parameter_id, value: String(v.value ?? '') })),
    };
    return await api.post('/api/process/recipe/instances', payload);
  },

  /**
   * Lee valores actuales del PLC de una instancia origen y crea una instancia nueva.
   * @param {object} opts
   * @param {number} opts.sourceInstanceId
   * @param {Array} opts.templateParameters - parámetros de la plantilla destino
   * @param {object} opts.body - campos de createInstance (sin values)
   * @param {'actual'|'recipe'} [opts.valueField='actual']
   */
  async createInstanceFromPlcSource({ sourceInstanceId, templateParameters, body, valueField = 'actual' }) {
    const plc = await this.readFromPLC(sourceInstanceId);
    const mapped = mapPlcValuesToFormValues(plc, templateParameters, valueField);
    const values = (templateParameters ?? []).map((p) => {
      const pid = p.id ?? p.ID;
      return { parameter_id: pid, value: mapped[pid] ?? '' };
    });
    return this.createInstance({ ...body, values });
  },

  /**
   * Obtiene una instancia de receta por ID.
   * @param {number} id - ID de la instancia
   * @returns {Promise<object|null>}
   */
  async getInstance(id) {
    const data = await api.get(`/api/process/recipe/instances/${id}`);
    return data || null;
  },

  /**
   * Actualiza una instancia (descripción y/o valores).
   * @param {number} id
   * @param {object} body - { description?, values: [{ parameter_id, value }] }
   * @returns {Promise<object>}
   */
  async updateInstance(id, body) {
    return await api.put(`/api/process/recipe/instances/${id}`, body);
  },

  /**
   * Elimina una instancia de receta.
   * @param {number} id
   * @returns {Promise<void>}
   */
  async deleteInstance(id) {
    return await api.delete(`/api/process/recipe/instances/${id}`);
  },

  /**
   * Lee desde el PLC los valores de receta y actuales para una instancia.
   * @param {number} instanceId
   * @returns {Promise<{ product_name, alternative, revision, description, values: Array<{ parameter_id, parameter_name, recipe, actual, unit, data_type }> }>}
   */
  async readFromPLC(instanceId) {
    if (USE_DUMMY_READ_FROM_PLC) {
      return Promise.resolve(getDummyReadFromPLCResponse(instanceId));
    }
    const data = await api.get(
      `/api/process/recipe/read-from-plc?instance_id=${encodeURIComponent(instanceId)}`
    );
    return data || { values: [] };
  },

  /**
   * Envía la receta de una instancia al PLC.
   * @param {number} instanceId
   * @returns {Promise<{ status?, product_name?, alternative?, revision?, description?, values? }>}
   */
  async sendToPlc(instanceId) {
    const data = await api.post('/api/process/recipe/send-to-plc', { instance_id: instanceId });
    return data || {};
  },
};
